import { AdminPortal, useAuth, useLoginWithRedirect } from '@frontegg/react';
import { Button, Stack } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { GenericFallbacksWrapper } from './components/Fallback/GenericFallbacksWrapper';
import { BootAwareLoader, HideBootLoader } from './components/Loader/BootLoader';
import { ModalProvider } from './components/Modal/ModalContext';
import { ToastMessageProvider } from './components/ToastMessage/ToastMessageProvider';
import { useDebugKeyboard } from './debug/useDebugKeyboard';
import { HelpMessage } from './pages/CompanyProfiles/components/Messages/HelpMessage';
import { PermissionService } from './services/PermissionService';
import { authenticatedUserState, earlyAccessTokenState } from './services/state/AuthState';
import { identifyLaunchDarklyEarly } from './util/earlyBoot';
import { isTokenExpired } from './util/jwt';

const TOKEN_REFRESH_GUARD = 'fs_token_refresh';
const OAUTH_CALLBACK_GUARD = 'fs_oauth_callback_retry';
const OAUTH_CALLBACK_PATH = '/oauth/callback';

// LaunchDarkly identifies by email, which the access token carries before Frontegg's user object arrives.
function useEarlyLaunchDarkly() {
  const earlyToken = useAtomValue(earlyAccessTokenState);
  const ldClient = useLDClient();

  useEffect(() => {
    if (earlyToken && ldClient) {
      identifyLaunchDarklyEarly(ldClient, earlyToken);
    }
  }, [earlyToken, ldClient]);
}

export function AuthWrapperFrontEgg(props: PropsWithChildren) {
  const { user: frontEggUser, isAuthenticated, isLoading: authLoading, error: authError } = useAuth();
  const loginWithRedirect = useLoginWithRedirect();
  const [user, setUser] = useAtom(authenticatedUserState);
  const setEarlyAccessToken = useSetAtom(earlyAccessTokenState);
  const navigate = useNavigate();
  const fronteggSpanRef = useRef<Sentry.Span | undefined>(
    authLoading ? Sentry.startInactiveSpan({ name: 'app.init.frontegg', op: 'app.init' }) : undefined
  );
  // A token refresh brings a new user object for the same person, so the portal opens once per user, not per token.
  const adminPortalShownForRef = useRef<string | null>(null);
  // Hosted login returns a live Frontegg session straight to the callback, so a token exchange that keeps failing would loop.
  const [callbackLoginFailed, setCallbackLoginFailed] = useState(false);
  // StrictMode and a new loginWithRedirect identity re-run the effect before the page leaves; only a fresh load is a failed retry.
  const callbackRedirectStartedRef = useRef(false);

  useDebugKeyboard();
  useEarlyLaunchDarkly();

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (fronteggSpanRef.current) {
      fronteggSpanRef.current.end();
      fronteggSpanRef.current = undefined;
    }

    // From here requests carry the user's token, or none: an early token would outlive a signed-out session.
    setEarlyAccessToken(undefined);

    if (isAuthenticated && frontEggUser) {
      if (frontEggUser.accessToken && isTokenExpired(frontEggUser.accessToken)) {
        if (!sessionStorage.getItem(TOKEN_REFRESH_GUARD)) {
          sessionStorage.setItem(TOKEN_REFRESH_GUARD, '1');
          localStorage.setItem('previousURL', `${window.location.pathname}${window.location.search}`);
          loginWithRedirect();
          return;
        }
        Sentry.captureMessage('Token still expired after redirect', {
          level: 'warning',
          tags: { feature: 'token-refresh-guard' },
        });
      } else {
        sessionStorage.removeItem(TOKEN_REFRESH_GUARD);
      }
      sessionStorage.removeItem(OAUTH_CALLBACK_GUARD);
      setCallbackLoginFailed(false);

      setUser(frontEggUser);
      PermissionService.initService(frontEggUser);

      if (PermissionService.get().isPortalOnly()) {
        if (adminPortalShownForRef.current !== frontEggUser.id) {
          adminPortalShownForRef.current = frontEggUser.id;
          AdminPortal.show();
        }
        return;
      }

      if (localStorage.getItem('previousURL')) {
        navigate(localStorage.getItem('previousURL')!);
        localStorage.removeItem('previousURL');
      }
    } else {
      adminPortalShownForRef.current = null;
      setUser(undefined);
      if (window.location.pathname === OAUTH_CALLBACK_PATH) {
        if (callbackRedirectStartedRef.current) {
          return;
        }
        if (sessionStorage.getItem(OAUTH_CALLBACK_GUARD)) {
          Sentry.captureMessage('OAuth callback still unauthenticated after redirect', {
            level: 'warning',
            tags: { feature: 'oauth-callback-guard' },
          });
          setCallbackLoginFailed(true);
          return;
        }
        sessionStorage.setItem(OAUTH_CALLBACK_GUARD, '1');
        callbackRedirectStartedRef.current = true;
      } else {
        localStorage.setItem('previousURL', `${window.location.pathname}${window.location.search}`);
      }
      loginWithRedirect();
    }
  }, [frontEggUser, isAuthenticated, authLoading, loginWithRedirect, navigate, setEarlyAccessToken, setUser]);

  if (callbackLoginFailed) {
    return (
      <AuthErrorMessage message={authError?.message ?? 'Sign-in could not be completed. Please try again.'} />
    );
  }

  if (authLoading || !user) {
    return <BootAwareLoader />;
  }

  if (authError) {
    return <AuthErrorMessage message={authError.message} />;
  }

  if (!PermissionService.isInitialized()) {
    return null;
  }
  if (PermissionService.get().isPortalOnly()) {
    return (
      <Stack>
        <HideBootLoader />
        <Button size='large' variant='contained' color='secondary' onClick={() => AdminPortal.show()}>
          Launch Admin Portal
        </Button>
      </Stack>
    );
  }

  return (
    <GenericFallbacksWrapper suspenseFallback={<BootAwareLoader height='100%' />}>
      <ToastMessageProvider>
        <ModalProvider>{props.children}</ModalProvider>
      </ToastMessageProvider>
    </GenericFallbacksWrapper>
  );
}

function AuthErrorMessage({ message }: { message: string }) {
  return (
    <>
      <HideBootLoader />
      <HelpMessage severity={'error'} message={'Authentication Error: ' + message} />
    </>
  );
}
